/* Copyright (c) 2026 Xperi. All Rights Reserved. */

const EPOCH_MILLIS_THRESHOLD: number = 1e12;
const MINUTES_PAD_LENGTH: number = 2;
const PAD_CHARACTER: string = "0";
const HOURS_IN_HALF_DAY: number = 12;

/**
 * Converts an airing timestamp (ISO-8601 string or epoch seconds/ms) into a
 * 12-hour wall-clock display string such as "9:30 PM".
 *
 * Epoch handling:
 * - Numbers > 1e12 are treated as epoch **milliseconds**.
 * - Numbers <= 1e12 are treated as epoch **seconds** and converted to ms.
 *
 * @param timestamp - ISO-8601 date string OR epoch number (seconds or ms).
 * @returns Formatted clock time string (e.g. "9:30 PM"), or empty string on
 *          invalid input.
 */
export function formatAiringClockTime(timestamp: string | number): string {
  if (timestamp === "") {
    return "";
  }

  let date: Date;

  if (typeof timestamp === "number") {
    if (!isFinite(timestamp) || isNaN(timestamp) || timestamp < 0) {
      return "";
    }
    // Epoch seconds vs milliseconds heuristic
    const epochMs: number =
      timestamp > EPOCH_MILLIS_THRESHOLD ? timestamp : timestamp * 1000;
    date = new Date(epochMs);
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) {
    return "";
  }

  const hours24: number = date.getHours();
  const minutes: number = date.getMinutes();
  const period: string = hours24 >= HOURS_IN_HALF_DAY ? "PM" : "AM";
  let hours12: number = hours24 % HOURS_IN_HALF_DAY;
  if (hours12 === 0) {
    hours12 = HOURS_IN_HALF_DAY;
  }

  const paddedMinutes: string = String(minutes).padStart(
    MINUTES_PAD_LENGTH,
    PAD_CHARACTER
  );

  return `${hours12}:${paddedMinutes} ${period}`;
}
